import { useNotificationPreferences, CATEGORY_LABELS, type NotifCategory } from '../../../hooks/useNotificationPreferences';

export const NotificationPreferencesTab = () => {
  const { prefs, loading, saving, saved, updatePref, savePrefs, setPrefs } = useNotificationPreferences();

  const categories = Object.keys(CATEGORY_LABELS) as NotifCategory[];

  const handleToggle = (category: NotifCategory, channel: 'system' | 'email', value: boolean) => {
    const updated = {
      ...prefs,
      [category]: { ...prefs[category], [channel]: value },
    };
    setPrefs(updated);
    updatePref(category, channel, value);
  };

  const handleSave = () => {
    savePrefs(prefs);
  };

  if (loading) {
    return (
      <div className="py-16 text-center">
        <i className="ri-loader-4-line animate-spin text-gray-400 text-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Intro */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start gap-3 mb-1">
          <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-notification-3-line text-emerald-600 text-base" />
          </div>
          <div>
            <h3 className="text-sm font-700 text-gray-900">Preferências de Notificações</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Configure como quer receber cada tipo de notificação do sistema.
            </p>
          </div>
        </div>
      </div>

      {/* Channel legend */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <p className="text-xs font-700 text-gray-500 uppercase tracking-wide mb-3">Canais disponíveis</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-3 flex-1 bg-emerald-50/60 border border-emerald-100 rounded-xl px-4 py-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
              <i className="ri-notification-3-line text-emerald-600 text-sm" />
            </div>
            <div>
              <p className="text-[13px] font-700 text-emerald-800">Sistema</p>
              <p className="text-[11px] text-emerald-600">Notificações dentro do app</p>
            </div>
            <span className="ml-auto text-[11px] font-700 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Ativo</span>
          </div>

          <div className="flex items-center gap-3 flex-1 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 opacity-60">
            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center shrink-0">
              <i className="ri-mail-line text-gray-500 text-sm" />
            </div>
            <div>
              <p className="text-[13px] font-700 text-gray-500">E-mail</p>
              <p className="text-[11px] text-gray-400">Envio por e-mail</p>
            </div>
            <span className="ml-auto text-[11px] font-700 px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full whitespace-nowrap">Em breve</span>
          </div>
        </div>
      </div>

      {/* Preferences per category */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Table header */}
        <div className="flex items-center px-5 py-3 border-b border-gray-100 bg-gray-50/60">
          <div className="flex-1">
            <p className="text-[11px] font-700 text-gray-500 uppercase tracking-wide">Tipo de notificação</p>
          </div>
          <div className="flex gap-6 shrink-0">
            <div className="w-16 text-center">
              <p className="text-[11px] font-700 text-emerald-600 uppercase tracking-wide">Sistema</p>
            </div>
            <div className="w-16 text-center">
              <p className="text-[11px] font-700 text-gray-400 uppercase tracking-wide flex items-center justify-center gap-1">
                E-mail
                <span className="text-[8px] bg-gray-200 text-gray-500 px-1 py-0 rounded-full font-700">Em breve</span>
              </p>
            </div>
          </div>
        </div>

        {categories.map((cat) => {
          const info = CATEGORY_LABELS[cat];
          const pref = prefs[cat];

          return (
            <div
              key={cat}
              className="flex items-center px-5 py-4 border-b border-gray-50 last:border-0 hover:bg-gray-50/40 transition-colors"
            >
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center shrink-0">
                  <i className={`${info.icon} text-gray-500 text-sm`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-600 text-gray-800 truncate">{info.label}</p>
                  <p className="text-[11px] text-gray-400 leading-snug hidden sm:block">{info.description}</p>
                </div>
              </div>

              <div className="flex gap-6 shrink-0 ml-4">
                {/* System toggle — active */}
                <div className="w-16 flex items-center justify-center">
                  <button
                    onClick={() => handleToggle(cat, 'system', !pref.system)}
                    className={`relative rounded-full transition-colors cursor-pointer overflow-hidden ${
                      pref.system ? 'bg-emerald-500' : 'bg-gray-200'
                    }`}
                    style={{ width: '40px', height: '22px', flexShrink: 0 }}
                    aria-label={`Toggle sistema ${info.label}`}
                  >
                    <span
                      className={`absolute top-0.5 w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform ${
                        pref.system ? 'translate-x-[20px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Email toggle — disabled (Em breve) */}
                <div className="w-16 flex items-center justify-center">
                  <div
                    className="relative cursor-not-allowed opacity-40"
                    title="Em breve"
                    style={{ width: '40px', height: '22px' }}
                  >
                    <div className="w-full h-full bg-gray-200 rounded-full">
                      <span className="absolute top-0.5 left-0.5 w-[18px] h-[18px] bg-white rounded-full shadow-sm" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Email future note */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-mail-send-line text-amber-500 text-sm" />
          </div>
          <div>
            <p className="text-[13px] font-600 text-gray-700">Notificações por e-mail em breve</p>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
              Estamos preparando o canal de e-mail. Em breve você poderá configurar individualmente quais notificações deseja receber também no seu e-mail.
            </p>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-600 cursor-pointer whitespace-nowrap transition-all ${
            saved
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-emerald-500 hover:bg-emerald-600 text-white'
          } disabled:opacity-50`}
        >
          {saving && <i className="ri-loader-4-line animate-spin" />}
          {saved ? <><i className="ri-check-line" />Preferências salvas!</> : 'Salvar preferências'}
        </button>
      </div>
    </div>
  );
};
